import { useEffect, useMemo, useState } from "react";
import { Receipt, Wallet, Camera, Rocket, BadgeCheck, RefreshCcw, Sparkles, Ticket, CalendarClock } from "lucide-react";
import { Seo } from "@/components/common/Seo";
import { PageHero } from "@/components/common/PageHero";
import { Reveal, RevealGroup, RevealItem } from "@/components/common/Reveal";
import { SectionLabel } from "@/components/common/SectionLabel";
import { CTAButton } from "@/components/common/CTAButton";
import { EditorialMarquee } from "@/components/common/EditorialMarquee";
import {
  SEO, BASE_RATE, FREQ_MAP, LENGTH_MULTIPLIERS, FREQ_MULTIPLIERS, REGISTER_URL,
} from "@/data/site";

const inr = (n) => `₹${n.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const PROMISES = [
  { icon: Receipt, title: "One published rate card", body: "Every advertiser sees the same numbers. No back-and-forth, no “special” rates, no salesperson in the loop." },
  { icon: BadgeCheck, title: "Pay per impression", body: "One ad play on one operational screen = one impression. You pay for reach that actually happened." },
  { icon: Wallet, title: "Prepaid wallet", body: "Load, launch, and watch the balance move in real time. No invoices, no minimum spend, no surprises." },
  { icon: Camera, title: "Playback evidence", body: "Detailed campaign and playback records support transparent reporting - your spend is backed by evidence, not a vague summary." },
  { icon: RefreshCcw, title: "Missed plays, made right", body: "Offline screen during your slot? That impression is compensated - extra plays or a wallet credit that never expires." },
  { icon: Rocket, title: "Live in 24 hours", body: "Upload, get approved, go live at 06:00 the next morning. Campaigns start in a day, not a quarter." },
];

const Calculator = () => {
  const [length, setLength] = useState(10);
  const [freq, setFreq] = useState(3);
  const [societies, setSocieties] = useState(5);
  const [screens, setScreens] = useState(6);

  const { rate, perDay, perWeek, perMonth, playsPerDay, impressionsPerDay } = useMemo(() => {
    const rate = BASE_RATE * length * FREQ_MAP[freq];
    const windowMinutes = 1050; // 06:00 – 23:30 daily window
    const playsPerDay = Math.floor(windowMinutes / freq);
    const totalScreens = societies * screens;
    const impressionsPerDay = playsPerDay * totalScreens;
    const perDay = impressionsPerDay * rate;
    return { rate, perDay, perWeek: perDay * 7, perMonth: perDay * 30, playsPerDay, impressionsPerDay };
  }, [length, freq, societies, screens]);

  const Chip = ({ active, onClick, children, testid }) => (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`px-4 py-2.5 rounded-md font-display font-600 text-sm transition-colors duration-200 border ${
        active ? "bg-teal text-ink border-teal" : "bg-transparent text-ink/60 border-ink/20 hover:border-ink/50"
      }`}
    >
      {children}
    </button>
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8" data-testid="pricing-calculator">
      <div className="lg:col-span-7 bg-white border border-ink/10 rounded-xl p-6 md:p-8">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.18em] text-ink/50 mb-3">Ad length</p>
          <div className="flex flex-wrap gap-2">
            {[5, 10, 15, 20, 25, 30].map((v) => (
              <Chip key={v} active={length === v} onClick={() => setLength(v)} testid={`calc-length-${v}`}>{v}s</Chip>
            ))}
          </div>
        </div>
        <div className="mt-7">
          <p className="font-mono text-xs uppercase tracking-[0.18em] text-ink/50 mb-3">Frequency</p>
          <div className="flex flex-wrap gap-2">
            {[1, 2, 3, 5].map((v) => (
              <Chip key={v} active={freq === v} onClick={() => setFreq(v)} testid={`calc-freq-${v}`}>Every {v} min</Chip>
            ))}
          </div>
        </div>
        <div className="mt-7 grid grid-cols-2 gap-6">
          <label className="block">
            <span className="font-mono text-xs uppercase tracking-[0.18em] text-ink/50">Societies</span>
            <input type="range" min="1" max="40" value={societies} onChange={(e) => setSocieties(+e.target.value)}
              data-testid="calc-societies" className="w-full mt-3 accent-teal" />
            <span className="font-display font-800 text-2xl">{societies}</span>
          </label>
          <label className="block">
            <span className="font-mono text-xs uppercase tracking-[0.18em] text-ink/50">Screens / society</span>
            <input type="range" min="1" max="20" value={screens} onChange={(e) => setScreens(+e.target.value)}
              data-testid="calc-screens" className="w-full mt-3 accent-teal" />
            <span className="font-display font-800 text-2xl">{screens}</span>
          </label>
        </div>
      </div>

      <div className="lg:col-span-5 bg-ink text-white rounded-xl p-6 md:p-8 flex flex-col">
        <p className="font-mono text-xs uppercase tracking-[0.18em] text-teal">Estimated screen impression rate</p>
        <p className="mt-3 font-display font-800 text-4xl" data-testid="calc-rate">
          {inr(rate)} <span className="text-white/50 text-base font-500">/ impression</span>
        </p>
        <p className="mt-2 text-sm text-white/45">
          An impression is counted when your ad plays on an operational screen.
        </p>

        <div className="mt-8 space-y-px bg-white/10 rounded-lg overflow-hidden">
          {[
            ["Plays / day", playsPerDay.toLocaleString("en-IN")],
            ["Impressions / day", impressionsPerDay.toLocaleString("en-IN")],
          ].map(([k, v]) => (
            <div key={k} className="flex justify-between bg-ink px-4 py-3 text-sm">
              <span className="text-white/55">{k}</span><span className="font-mono">{v}</span>
            </div>
          ))}
        </div>

        <div className="mt-6 grid grid-cols-3 gap-2 text-center">
          {[["Daily", perDay], ["Weekly", perWeek], ["Monthly", perMonth]].map(([k, v]) => (
            <div key={k} className="border border-white/10 rounded-lg p-3">
              <p className="font-mono text-[10px] uppercase tracking-wider text-white/45">{k}</p>
              <p className="mt-1 font-display font-700 text-teal text-sm md:text-base" data-testid={`calc-${k.toLowerCase()}`}>{inr(Math.round(v))}</p>
            </div>
          ))}
        </div>
        <p className="mt-6 text-xs text-white/40 leading-relaxed">
          Estimate only, excludes GST, and uses standard-hour rates. Plays in prime-time bands (07:00–10:00 and 18:00–22:00) carry a 25% premium; coupon codes apply at checkout. Pricing scales with the number of screens in each selected community.
        </p>
      </div>
    </div>
  );
};

const PricingPromise = () => (
  <section className="bg-paper py-24 md:py-32">
    <div className="container-px">
      <Reveal><SectionLabel>Why Advertisers Choose AdX</SectionLabel></Reveal>
      <Reveal delay={0.05}>
        <h2 className="mt-6 font-display font-800 text-4xl md:text-6xl tracking-tight max-w-3xl text-balance">
          Honest pricing is the whole pitch.
        </h2>
      </Reveal>
      <Reveal delay={0.08}>
        <p className="mt-6 max-w-2xl text-ink/60 leading-relaxed">
          Most residential DOOH is sold on relationships and negotiated in the dark. AdX flips it - transparent pricing, per-impression billing, and playback evidence. You always know what you paid for.
        </p>
      </Reveal>
      <RevealGroup className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {PROMISES.map((p) => (
          <RevealItem key={p.title} className="group bg-white border border-ink/10 rounded-xl p-8 transition-transform duration-300 hover:-translate-y-1">
            <p.icon className="h-8 w-8 text-teal" strokeWidth={1.5} />
            <h3 className="mt-6 font-display font-700 text-xl">{p.title}</h3>
            <p className="mt-3 text-ink/60 leading-relaxed">{p.body}</p>
          </RevealItem>
        ))}
      </RevealGroup>
    </div>
  </section>
);

const BaseRate = () => (
  <section className="relative bg-ink text-white py-24 md:py-32 overflow-hidden">
    <div className="pointer-events-none absolute -top-24 right-1/4 h-80 w-80 rounded-full bg-teal/15 blur-[130px]" />
    <div className="relative container-px grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
      <div className="lg:col-span-6">
        <SectionLabel dark>The Rate Card</SectionLabel>
        <h2 className="mt-6 font-display font-800 text-4xl md:text-6xl tracking-tight text-balance">
          It starts at one paisa.
        </h2>
        <p className="mt-6 text-white/65 leading-relaxed max-w-lg">
          Pricing starts at just 1 paisa per second, per screen. Your final cost depends simply on your ad length, frequency, and the communities you choose - the calculator does the maths for you.
        </p>
        <div className="mt-8"><CTAButton href={REGISTER_URL} variant="primary" data-testid="pricing-create-account">Create account</CTAButton></div>
      </div>
      <div className="lg:col-span-6">
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-8 md:p-10">
          <p className="font-mono text-xs uppercase tracking-[0.18em] text-teal">Starts at</p>
          <p className="mt-3 font-display font-800 text-6xl md:text-7xl tracking-tight">1p<span className="text-white/50 text-2xl font-500"> / sec</span></p>
          <p className="mt-2 text-white/50">per second, per screen impression</p>
          <div className="mt-8 font-mono text-sm text-white/70 bg-ink rounded-lg border border-white/10 p-5">
            cost = seconds <span className="text-teal">×</span> frequency <span className="text-teal">×</span> screens
          </div>
        </div>
      </div>
    </div>
  </section>
);

const RateTables = () => (
  <section className="bg-white py-24 md:py-32 border-b border-ink/10">
    <div className="container-px">
      <Reveal><SectionLabel>Formats &amp; Frequency</SectionLabel></Reveal>
      <Reveal delay={0.05}>
        <h2 className="mt-6 font-display font-800 text-4xl md:text-5xl tracking-tight max-w-3xl text-balance">
          Choose your format. Pick your rhythm.
        </h2>
      </Reveal>
      <Reveal delay={0.08}>
        <p className="mt-6 max-w-2xl text-ink/60 leading-relaxed">
          One rate card for everyone - your cost is decided by the format you choose, never by negotiation. The calculator below does the maths.
        </p>
      </Reveal>
      <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="rounded-xl border border-ink/10 overflow-hidden">
          <div className="bg-ink text-teal px-5 py-3 font-mono text-xs uppercase tracking-[0.18em]">Ad Lengths</div>
          <table className="w-full text-sm">
            <tbody>
              {LENGTH_MULTIPLIERS.map((r) => (
                <tr key={r.len} className="border-t border-ink/10">
                  <td className="px-5 py-3 font-display font-600">{r.len}</td>
                  <td className="px-5 py-3 text-right text-ink/50">{r.slots}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="px-5 py-4 text-xs text-ink/50 leading-relaxed border-t border-ink/10">
            Every 5 seconds is one unit of the loop. Longer creatives simply use more units.
          </p>
        </div>
        <div className="rounded-xl border border-ink/10 overflow-hidden">
          <div className="bg-ink text-teal px-5 py-3 font-mono text-xs uppercase tracking-[0.18em]">Frequency Options</div>
          <table className="w-full text-sm">
            <tbody>
              {FREQ_MULTIPLIERS.map((r) => (
                <tr key={r.freq} className="border-t border-ink/10">
                  <td className="px-5 py-3 font-display font-600">{r.freq}</td>
                  <td className="px-5 py-3 text-right text-ink/50">{r.desc}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="px-5 py-4 text-xs text-ink/50 leading-relaxed border-t border-ink/10">
            How often your ad plays within each community's master cycle.
          </p>
        </div>
      </div>
    </div>
  </section>
);

const PrimeTime = () => (
  <section className="relative bg-ink text-white py-24 md:py-32 overflow-hidden">
    <div className="pointer-events-none absolute -bottom-24 left-1/4 h-80 w-80 rounded-full bg-teal/15 blur-[130px]" />
    <div className="relative container-px grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
      <div className="lg:col-span-6">
        <SectionLabel dark>Prime Time</SectionLabel>
        <h2 className="mt-6 font-display font-800 text-4xl md:text-6xl tracking-tight text-balance">
          The hours every eye is waiting.
        </h2>
        <p className="mt-6 text-white/65 leading-relaxed max-w-lg">
          Lobbies are at their busiest when communities leave for the day and return home. Plays scheduled inside prime-time bands carry a 25% premium on the base rate - because that's when your ad works its hardest. Every other hour runs at the standard rate.
        </p>
      </div>
      <div className="lg:col-span-6">
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-8 md:p-10">
          <div className="space-y-px bg-white/10 rounded-lg overflow-hidden">
            {[
              ["Standard hours", "All day, outside prime bands", "1p / sec"],
              ["Morning prime", "07:00 – 10:00", "1.25p / sec"],
              ["Evening prime", "18:00 – 22:00", "1.25p / sec"],
            ].map(([k, band, rate]) => (
              <div key={k} className="flex items-center justify-between gap-4 bg-ink px-5 py-4">
                <div>
                  <p className="font-display font-600">{k}</p>
                  <p className="font-mono text-xs text-white/45 mt-0.5">{band}</p>
                </div>
                <p className={`font-mono text-sm ${rate.startsWith("1.25") ? "text-teal" : "text-white/70"}`}>{rate}</p>
              </div>
            ))}
          </div>
          <p className="mt-5 text-xs text-white/40 leading-relaxed">
            Prime-time billing applies automatically to plays inside these bands - the campaign report shows exactly which impressions ran where.
          </p>
        </div>
      </div>
    </div>
  </section>
);

const Coupons = () => (
  <section className="bg-paper py-24 md:py-32">
    <div className="container-px">
      <Reveal><SectionLabel>Coupons &amp; Offers</SectionLabel></Reveal>
      <Reveal delay={0.05}>
        <h2 className="mt-6 font-display font-800 text-4xl md:text-6xl tracking-tight max-w-3xl text-balance">
          Discount Coupons for Everyone. More you spend. More you save!
        </h2>
      </Reveal>
      <Reveal delay={0.08}>
        <p className="mt-6 max-w-2xl text-ink/60 leading-relaxed">
          Our rate card is identical for every advertiser - so instead of published discounts, registered advertisers receive coupon codes from time to time. Apply a code at checkout and the benefit reflects in your wallet before you spend a rupee.
        </p>
      </Reveal>
      <RevealGroup className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
        {[
          { icon: Ticket, title: "Duration coupons", body: "Longer campaign commitments periodically unlock codes - the weeks you book do the talking." },
          { icon: CalendarClock, title: "Frequency coupons", body: "High-frequency plans earn codes from time to time, rewarding advertisers who show up often." },
          { icon: Sparkles, title: "Festive offers", body: "Diwali, New Year and seasonal calendars bring limited-time codes - exactly when lobbies are fullest." },
        ].map((c) => (
          <RevealItem key={c.title} className="group bg-white border border-ink/10 rounded-xl p-8 transition-transform duration-300 hover:-translate-y-1">
            <c.icon className="h-8 w-8 text-teal" strokeWidth={1.5} />
            <h3 className="mt-6 font-display font-700 text-xl">{c.title}</h3>
            <p className="mt-3 text-ink/60 leading-relaxed">{c.body}</p>
          </RevealItem>
        ))}
      </RevealGroup>
      <Reveal delay={0.1}>
        <p className="mt-10 text-sm text-ink/50">
          Codes are shared with registered advertisers first. <a href={REGISTER_URL} className="text-teal font-600 underline underline-offset-4 decoration-teal/40 hover:decoration-teal transition-colors">Create an account</a> to get on the list.
        </p>
      </Reveal>
    </div>
  </section>
);

const CalculatorSection = () => (
  <section className="bg-paper py-24 md:py-32">
    <div className="container-px">
      <Reveal><SectionLabel>Pricing Calculator</SectionLabel></Reveal>
      <Reveal delay={0.05}>
        <h2 className="mt-6 font-display font-800 text-4xl md:text-6xl tracking-tight max-w-3xl text-balance">
          Estimate your campaign in seconds.
        </h2>
      </Reveal>
      <div className="mt-14"><Calculator /></div>
      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="rounded-xl border border-ink/10 bg-white p-8">
          <h3 className="font-display font-700 text-xl">How impressions work</h3>
          <p className="mt-4 text-ink/60 leading-relaxed">
            One impression = one ad play on one operational screen. If a society has 6 operational screens and your ad plays once, that's 6 impressions. Offline screens don't count.
          </p>
        </div>
        <div className="rounded-xl border border-ink/10 bg-white p-8">
          <h3 className="font-display font-700 text-xl">Payment</h3>
          <p className="mt-4 text-ink/60 leading-relaxed">
            100% prepaid wallet. All prices exclude GST. No hidden charges and no minimum spend beyond your wallet balance. Compensation credits for missed impressions never expire.
          </p>
        </div>
      </div>
    </div>
  </section>
);

const FinalCta = () => (
  <section className="bg-paper py-24 md:py-32">
    <div className="container-px">
      <div className="rounded-2xl bg-ink text-white p-10 md:p-16 relative overflow-hidden">
        <div className="pointer-events-none absolute -bottom-24 -right-10 h-72 w-72 rounded-full bg-teal/15 blur-[120px]" />
        <div className="relative max-w-2xl">
          <h2 className="font-display font-800 text-4xl md:text-5xl tracking-tight text-balance">
            Your first campaign is minutes away.
          </h2>
          <p className="mt-5 text-white/65 leading-relaxed">
            Local business or national brand - same transparent pricing, same playback evidence. Start self-serve, or let our team plan a multi-society campaign with you.
          </p>
          <div className="mt-9 flex flex-col sm:flex-row gap-4">
            <CTAButton href={REGISTER_URL} variant="primary" data-testid="pricing-final-create">Create account</CTAButton>
            <CTAButton to="/contact" variant="ghostLight" data-testid="pricing-final-sales">Talk to sales</CTAButton>
          </div>
        </div>
      </div>
    </div>
  </section>
);

export default function Pricing() {
  useEffect(() => {
    window.dataLayer?.push({ event: "pricing_view" });
  }, []);

  return (
    <>
      <Seo title={SEO.pricing.title} description={SEO.pricing.description} />
      <PageHero
        label="Advertiser Pricing"
        titleLines={["Pay Per Impression.", "Nothing Else."]}
        accentIndex={1}
        subtitle="Transparent, prepaid advertising across residential communities. Choose your ad length, frequency and communities - and pay based on the advertising inventory you use."
      >
        <div className="flex flex-col gap-6">
          <p className="font-display font-700 text-xl text-teal">Starts at 1 paisa per second.</p>
          <div className="flex flex-col sm:flex-row gap-4">
            <CTAButton href={REGISTER_URL} variant="primary" data-testid="pricing-hero-create">Create account</CTAButton>
            <CTAButton to="/societies" variant="ghostLight" data-testid="pricing-hero-society">Are you a society?</CTAButton>
          </div>
        </div>
      </PageHero>
      <EditorialMarquee items={["1 paisa / second", "Prepaid wallet", "No hidden fees", "GST-compliant", "Per-impression", "Playback evidence"]} />
      <PricingPromise />
      <BaseRate />
      <RateTables />
      <PrimeTime />
      <Coupons />
      <CalculatorSection />
      <FinalCta />
    </>
  );
}
